import { Renderer } from './renderer';
import { InputHandler } from './input';
import { TouchControls } from './touch-controls';
import { TitleScene } from './scenes/title-scene';
import { FightScene } from './scenes/fight-scene';
import { logger } from './logger';
import { audio } from './audio';
import { CANVAS_WIDTH, CANVAS_HEIGHT, FRAME_TIME } from '@/shared/constants';

type SceneType = 'title' | 'fight';

function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

function scaleCanvas(canvas: HTMLCanvasElement): void {
  const wrapper = canvas.parentElement;
  if (!wrapper) return;

  const wrapperW = wrapper.clientWidth;
  const wrapperH = wrapper.clientHeight;
  const scaleX = wrapperW / CANVAS_WIDTH;
  const scaleY = wrapperH / CANVAS_HEIGHT;
  const scale = Math.min(scaleX, scaleY, 1);

  canvas.style.width = `${CANVAS_WIDTH * scale}px`;
  canvas.style.height = `${CANVAS_HEIGHT * scale}px`;

  // Position the site link just below the canvas
  const link = document.getElementById('site-link');
  if (link) {
    const canvasH = CANVAS_HEIGHT * scale;
    const top = (wrapperH + canvasH) / 2;
    link.style.top = `${top}px`;
  }
}

export class Game {
  private renderer: Renderer;
  private input: InputHandler;
  private touchControls: TouchControls | null = null;
  private titleScene: TitleScene | null = null;
  private fightScene: FightScene | null = null;
  private currentScene: SceneType = 'title';
  private lastTime = 0;
  private accumulator = 0;
  private running = false;
  private mobile: boolean;
  private canvas: HTMLCanvasElement | null = null;

  constructor(private container: HTMLElement) {
    this.renderer = new Renderer();
    this.input = new InputHandler();
    this.mobile = isTouchDevice();
  }

  /**
   * Initialize PixiJS and start the game.
   * PixiJS v8 requires async initialization.
   */
  async start(): Promise<void> {
    // Initialize PixiJS renderer
    this.canvas = await this.renderer.init(this.container);
    logger.info('PixiJS renderer initialized');

    // Remove the original placeholder canvas if it exists
    const oldCanvas = document.getElementById('game-canvas');
    if (oldCanvas && oldCanvas !== this.canvas) {
      oldCanvas.remove();
    }

    // Set up canvas ID for consistency
    this.canvas.id = 'game-canvas';

    // Detect WebGL context loss (common mobile crash vector)
    this.canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      logger.error('WebGL context lost — game rendering will stop');
      this.running = false;
    });
    this.canvas.addEventListener('webglcontextrestored', () => {
      logger.warn('WebGL context restored — attempting to resume');
      this.running = true;
      this.lastTime = performance.now();
      requestAnimationFrame((t) => this.loop(t));
    });

    // Start loading soundtrack
    audio.preload();
    audio.initToggleButton();

    // Create title scene
    this.titleScene = new TitleScene(this.mobile);
    this.titleScene.setRenderer(this.renderer);

    // Set up touch controls
    if (this.mobile) {
      this.touchControls = new TouchControls();
      this.input.setTouchControls(this.touchControls);
      logger.info('Touch controls initialized');
    }

    // Input event listeners
    window.addEventListener('keydown', (e) => {
      audio.unlock();
      if (e.code === 'Enter') {
        this.handleStart();
      }
      if (e.code === 'KeyM') {
        audio.toggleMute();
      }
    });

    this.canvas.addEventListener(
      'touchstart',
      (e) => {
        e.preventDefault();
        audio.unlock();
        const touch = e.touches[0];
        if (touch && this.tryShareClick(touch.clientX, touch.clientY)) return;
        this.handleStart();
      },
      { passive: false },
    );

    this.canvas.addEventListener('click', (e) => {
      audio.unlock();
      if (this.tryShareClick(e.clientX, e.clientY)) return;
      this.handleStart();
    });

    // Responsive scaling
    scaleCanvas(this.canvas);
    window.addEventListener('resize', () => {
      if (this.canvas) scaleCanvas(this.canvas);
    });
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        if (this.canvas) scaleCanvas(this.canvas);
      }, 100);
    });

    // Pause/resume audio when tab visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        audio.pause();
      } else {
        audio.resume();
      }
    });

    // Start game loop
    this.running = true;
    this.lastTime = performance.now();
    logger.info('Game loop starting');
    requestAnimationFrame((t) => this.loop(t));
  }

  stop(): void {
    this.running = false;
  }

  /**
   * Convert a screen-space click to canvas coordinates and forward
   * to the fight scene's share button hit test.
   * Returns true if the click was consumed.
   */
  private tryShareClick(clientX: number, clientY: number): boolean {
    if (!this.canvas || !this.fightScene) return false;

    const rect = this.canvas.getBoundingClientRect();
    const scaleX = CANVAS_WIDTH / rect.width;
    const scaleY = CANVAS_HEIGHT / rect.height;
    const cx = (clientX - rect.left) * scaleX;
    const cy = (clientY - rect.top) * scaleY;

    return this.fightScene.handleClick(cx, cy);
  }

  private handleStart(): void {
    try {
      if (this.currentScene === 'title') {
        this.renderer.removeFighterDisplays('title-');
        this.renderer.hitEffects.clear();
        this.renderer.applyScreenShake(0, 0);
        this.fightScene = new FightScene(this.input, this.mobile);
        this.fightScene.setRenderer(this.renderer);
        this.currentScene = 'fight';
        this.touchControls?.show();
        logger.info('Transitioned to fight scene');
      } else if (this.fightScene?.isMatchOver) {
        this.renderer.removeFighterDisplays('fight-');
        this.renderer.hitEffects.clear();
        this.renderer.applyScreenShake(0, 0);
        this.currentScene = 'title';
        this.fightScene = null;
        this.titleScene = new TitleScene(this.mobile);
        this.titleScene.setRenderer(this.renderer);
        this.touchControls?.hide();
        logger.info('Transitioned to title scene');
      }
    } catch (err) {
      logger.error('Scene transition failed', err);
    }
  }

  private loop(time: number): void {
    if (!this.running) return;

    try {
      const delta = time - this.lastTime;
      this.lastTime = time;
      this.accumulator += delta;

      // Safety cap: prevent spiral of death if tab was backgrounded
      if (this.accumulator > FRAME_TIME * 10) {
        this.accumulator = FRAME_TIME;
      }

      while (this.accumulator >= FRAME_TIME) {
        this.update();
        this.accumulator -= FRAME_TIME;
      }

      this.draw();
    } catch (err) {
      logger.error('Game loop error', err);
    }

    requestAnimationFrame((t) => this.loop(t));
  }

  private update(): void {
    try {
      if (this.currentScene === 'title') {
        this.titleScene?.update();
      } else if (this.fightScene) {
        this.fightScene.update();
      }
    } catch (err) {
      logger.error('Update error', err);
    }
  }

  private draw(): void {
    try {
      // Clear temporary UI objects from previous frame
      this.renderer.beginFrame();

      if (this.currentScene === 'title') {
        this.titleScene?.draw(this.renderer);
      } else if (this.fightScene) {
        this.fightScene.draw(this.renderer);
      }

      // Tell PixiJS to render
      this.renderer.render();
    } catch (err) {
      logger.error('Draw error', err);
    }
  }
}
